import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Button, Card, Collapse, Grid, Stack, styled, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { assetService } from "../../../Core/Api/services";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import AssetIcon from "../../Misc/AssetIcon/asset-icon";
import AssetThumbnail from "../../Misc/AssetThumbnail";
import RotatableBox from "./rotatable-box";

interface AssetAndChildrenCardBaseProps {
  asset: DbAssetObjectInfos;
  action?: "select" | "edit" | "delete";
}

export interface AssetAndChildrenCardNoneProps extends AssetAndChildrenCardBaseProps {
  action?: undefined;
}

export interface AssetAndChildrenCardSelectProps extends AssetAndChildrenCardBaseProps {
  action: "select";
  selected: boolean;
  onSelect: (asset: DbAssetObjectInfos) => void;
}

export interface AssetAndChildrenCardEditProps extends AssetAndChildrenCardBaseProps {
  action: "edit";
  onEdit: (asset: DbAssetObjectInfos) => void;
}

export interface AssetAndChildrenCardDeleteProps extends AssetAndChildrenCardBaseProps {
  action: "delete";
  onDelete: (asset: DbAssetObjectInfos) => void;
}

type AssetAndChildrenCardProps =
  | AssetAndChildrenCardNoneProps
  | AssetAndChildrenCardSelectProps
  | AssetAndChildrenCardEditProps
  | AssetAndChildrenCardDeleteProps;

function isSelectAction(props: AssetAndChildrenCardProps): props is AssetAndChildrenCardSelectProps {
  return props.action === "select";
}

function isEditAction(props: AssetAndChildrenCardProps): props is AssetAndChildrenCardEditProps {
  return props.action === "edit";
}

function isDeleteAction(props: AssetAndChildrenCardProps): props is AssetAndChildrenCardDeleteProps {
  return props.action === "delete";
}

const AssetAndChildrenCard = (props: AssetAndChildrenCardProps) => {
  const { t } = useTranslation();
  const { asset: _asset } = props;

  const enqueueSnackbar = useCustomSnackbar();

  const [asset, setAsset] = React.useState(_asset);
  const [expandChilds, setExpandChilds] = React.useState(false);

  const handleExpandChilds = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    if (childrenCount > 0) {
      e.stopPropagation(); // Only stopPropagation if there are children => permit to select the card by clicking anywhere on it
      setExpandChilds(!expandChilds);
    }
  };

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isSelectAction(props)) {
      props.onSelect(asset);
    }
  };
  const isSelected = React.useMemo(() => {
    if (isSelectAction(props)) {
      return props.selected;
    }
    return false;
  }, [props]);

  const childrenCount = React.useMemo(() => asset.childObjects?.length ?? 0, [asset.childObjects]);

  React.useEffect(() => {
    assetService.GetChildrenByObjectId(asset.id, GlobalConst.MAX_CHILDREN).then((assetChildrensResponse) => {
      if (!assetChildrensResponse.success) {
        enqueueSnackbar(t("alert:error.fetchingAssetChildren", { message: assetChildrensResponse.message }), "error");
        return;
      }
      // Extract asset childrens
      const { childObjects, ...copiedAsset } = asset;
      // Copy asset without its children
      const newAsset = new DbAssetObjectInfos(copiedAsset);
      newAsset.childObjects = assetChildrensResponse.children;

      setAsset(newAsset);
    });
  }, [asset.id]);

  return (
    <StyledCard
      isSelectable={isSelectAction(props)}
      selected={isSelected}
      onClick={handleCardClick}
      sx={{ paddingBottom: childrenCount === 0 ? 1 : undefined }}
    >
      <Grid container spacing={1} justifyContent="flex-start" alignItems="flex-start">
        <Grid item xs={1}>
          <AssetThumbnail type={asset.type} size="medium" />
        </Grid>
        <Grid item xs={10}>
          <Stack spacing={1} pl={1}>
            <Typography variant="body1" fontWeight={500}>
              {asset.name}
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              onClick={handleExpandChilds}
              sx={{ cursor: !isSelectAction(props) && childrenCount === 0 ? "default" : "pointer" }}
            >
              <Typography variant="body2" color="primary.main" fontWeight={500}>
                {t("page:body.childrenIncluded", { count: childrenCount })}
              </Typography>
              {childrenCount > 0 && (
                <RotatableBox rotated={expandChilds} from={0} to={-180}>
                  <ExpandMoreRoundedIcon color="primary" />
                </RotatableBox>
              )}
            </Stack>

            <Collapse in={expandChilds}>
              <Grid container spacing={0.5} mb={1}>
                {asset.childObjects?.map((child) => (
                  <Grid item key={child.id} xs={6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AssetIcon type={child.type} colored />
                      <Typography
                        variant="body2"
                        sx={{
                          wordBreak: "break-all",
                          overflowWrap: "break-word",
                          wordWrap: "break-word",
                          whiteSpace: "normal",
                          display: "inline-block",
                        }}
                      >
                        {child.name}
                      </Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Collapse>
          </Stack>
        </Grid>
        <Grid item xs={1} alignItems="center" justifyContent="flex-end">
          {isSelected && <DoneOutlinedIcon color="primary" sx={{ marginTop: 1 }} />}
          {isEditAction(props) && (
            <Button onClick={() => props.onEdit(asset)} sx={{ marginTop: 0.5 }}>
              <EditOutlinedIcon />
            </Button>
          )}
          {isDeleteAction(props) && (
            <Button onClick={() => props.onDelete(asset)} sx={{ marginTop: 0.5 }}>
              <DeleteOutlinedIcon />
            </Button>
          )}
        </Grid>
      </Grid>
    </StyledCard>
  );
};

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "isSelectable" && prop !== "selected",
})<{ isSelectable: boolean; selected: boolean }>(({ theme, isSelectable, selected }) => ({
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(0),
  width: "100%",
  textAlign: "left",
  alignItems: "flex-start",
  justifyContent: "flex-start",
  borderRadius: 8,
  backgroundColor: "transparent",
  border: "1px solid",
  borderColor: theme.palette.backgroundAlpha.border,
  cursor: isSelectable ? "pointer" : "default",
  ...(selected && {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.alpha,
  }),
}));

export default AssetAndChildrenCard;
